import { useState, useEffect, useMemo } from "react";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
  BarChart as ReBarChart,
  Cell,
  LabelList,
  ReferenceArea,
  CartesianGrid
} from "recharts";
import { TYPOGRAPHY_VARIANTS } from "@certa/blocks/thanos";
import {
  CustomAxesTick,
  CustomYAxesTick
} from "../../customAxesTick/CustomAxesTick";
import { activeXLabel, lineStyle, tooltipCSS } from "./Barchart.styles";
import { UNIQUE_X_AXIS_IDENTIFIER } from "../constants";
import { getYAxesWidthMapping } from "./BarChart.utils";
import {
  CustomLegendWrapper,
  useGetLegendDataCommon
} from "../../customLegend/CustomLegend";
import { SHOW_ALL_TICKS, commonChartMargin } from "../../../constants";
import {
  CustomXAxesLabel,
  CustomYAxesLabel
} from "../../customAxesLabel/CustomAxesLabel";
import { MultiXAxisCustomTooltip } from "../../tooltip/Tooltip";
import {
  axisFormatter,
  getLabelsFromChartData,
  tooltipFormatter,
  xAxisFormatter
} from "../../../utils/chartUtils";
import type { ChartClickEvent, ChartComponentProps } from "@certa/common/types";
import { INDEX_ZERO } from "@certa/common/constants";
import { useChartReorder } from "./hooks/useChartReorder";

export const SimpleStackedBarChartHorizantal = (props: ChartComponentProps) => {
  const {
    // The default value of isAnimationActive is set to false, therefore the
    // isAnimationActive parameter is not required in all test cases.
    // Only in test scenarios, if isAnimationActive is true, the value label will not be shown.
    isAnimationActive = false,
    xAxisDataLabels,
    chartData,
    height = 500,
    onClick,
    xActiveIdentifier,
    otherConfig,
    sorting,
    handleSorterChange,
    onReorder
  } = props;

  const { probableDataType, xAxisKey } = chartData;
  const [chartXLabels, setChartXLabels] = useState<string[]>([]);
  const [showValueFor, setShowValueFor] = useState<string | null>(null);
  const xAxisDataLabelsLength = xAxisDataLabels.length;
  const isMultiXAxis = xAxisDataLabelsLength > 1;

  const {
    chartRef,
    chartContainerRef,
    isDraggingRef,
    orderedData,
    orderedDataWithUniqueXAxis,
    handleMouseDown,
    getCellColor,
    getCellCursor
  } = useChartReorder({
    data: chartData.data,
    initialOrder: otherConfig.dataOrder,
    onReorder,
    orientation: "horizontal"
  });

  useEffect(() => {
    setChartXLabels(
      getLabelsFromChartData(chartData.data).filter(
        label => !xAxisDataLabels.includes(label)
      )
    );
  }, [chartData.data, xAxisDataLabels]);

  const {
    colors,
    shouldShowValueLabels,
    showValueOn,
    showLegend,
    xAxis: otherConfigXAxis,
    xAxisLabel,
    yAxisLabel,
    showXAxisLabel,
    showYAxisLabel,
    valueLabelAngle
  } = otherConfig;

  useEffect(() => {
    setShowValueFor(null);
  }, [showValueOn]);

  const legendData = useGetLegendDataCommon({
    colors: colors,
    labels: chartXLabels,
    xAxisKey,
    fillOpacity: 0.6
  });

  const multiXAxisLabelFormatter = useMemo(
    () =>
      xAxisDataLabels.reduce((acc, label, index) => {
        Object.assign(acc, {
          [label]: xAxisFormatter(
            otherConfigXAxis?.dataTypes?.[index],
            otherConfigXAxis?.labelOutputTypes?.[index]
          )
        });
        return acc;
      }, {}),
    [
      otherConfigXAxis?.dataTypes,
      otherConfigXAxis?.labelOutputTypes,
      xAxisDataLabels
    ]
  );

  const yAxesWidthMapping = useMemo(
    () =>
      getYAxesWidthMapping({
        data: orderedData,
        dataTypes: otherConfigXAxis?.dataTypes,
        labelOutputTypes: otherConfigXAxis?.labelOutputTypes,
        xAxisDataLabels
      }),
    [
      xAxisDataLabels,
      otherConfigXAxis?.dataTypes,
      otherConfigXAxis?.labelOutputTypes,
      orderedData
    ]
  );

  return (
    <CustomLegendWrapper
      sorting={sorting}
      handleSorterChange={newSorting => {
        // Clear reorder when sorting
        if (newSorting) {
          onReorder?.([]);
        }
        handleSorterChange?.(newSorting);
      }}
      data={legendData}
      showLegend={showLegend}
    >
      <ResponsiveContainer
        width="100%"
        height={height}
        className={showValueOn === "hover" ? lineStyle : activeXLabel}
        ref={chartContainerRef}
      >
        <ReBarChart
          ref={chartRef}
          data={orderedDataWithUniqueXAxis}
          onClick={(data: Omit<ChartClickEvent, "activeXIdentifier">) => {
            if (onClick && data) {
              onClick({
                ...data,
                activeXIdentifier:
                  data.activePayload?.[INDEX_ZERO]?.payload?.[
                    UNIQUE_X_AXIS_IDENTIFIER
                  ] ?? data.activeLabel
              });
            }
          }}
          margin={commonChartMargin}
          layout="vertical"
        >
          <CartesianGrid
            strokeDasharray="4"
            stroke="#DFE2E7"
            vertical={false}
          />
          <XAxis
            type="number"
            fontSize="12"
            tickLine={false}
            tickSize={15}
            tickFormatter={axisFormatter(probableDataType, "MMM DD, YYYY")}
            fontWeight={400}
            interval="preserveStartEnd"
            tick={CustomAxesTick}
            stroke="#DFE2E7"
            label={
              <CustomXAxesLabel
                label={yAxisLabel}
                showAxisLabel={showYAxisLabel}
              />
            }
          />
          {xAxisDataLabels.map((label, index) => (
            <YAxis
              key={label}
              type="category"
              fontSize="12"
              yAxisId={index}
              dataKey={label}
              tickSize={15}
              tickLine={false}
              width={yAxesWidthMapping[label]}
              fontWeight={400}
              tickFormatter={xAxisFormatter(
                otherConfigXAxis?.dataTypes?.[index],
                otherConfigXAxis?.labelOutputTypes?.[index],
                label,
                orderedData,
                xAxisDataLabels[xAxisDataLabels.length - 1]
              )}
              interval={isMultiXAxis ? SHOW_ALL_TICKS : "preserveStartEnd"}
              label={
                <CustomYAxesLabel
                  label={xAxisLabel}
                  showAxisLabel={
                    xAxisDataLabelsLength !== index + 1
                      ? "none"
                      : showXAxisLabel
                  }
                />
              }
              tick={CustomYAxesTick}
            />
          ))}
          <YAxis
            type="category"
            yAxisId={xAxisDataLabels.length}
            hide
            dataKey={UNIQUE_X_AXIS_IDENTIFIER}
          />
          <Tooltip
            active={isDraggingRef.current ? false : undefined}
            formatter={tooltipFormatter("BAR_CHART", probableDataType)}
            content={
              <MultiXAxisCustomTooltip
                multiXAxisLabelFormatter={multiXAxisLabelFormatter}
                xAxisDataLabels={xAxisDataLabels}
              />
            }
            wrapperClassName={tooltipCSS(height)}
            cursor={{
              fill: "var(--neutral-10)",
              style: onClick ? { cursor: "pointer", pointerEvents: "all" } : {}
            }}
          />
          <ReferenceArea
            yAxisId={xAxisDataLabels.length}
            y1={xActiveIdentifier}
            y2={xActiveIdentifier}
            fill="var(--neutral-50)"
            fillOpacity={1}
            label={{
              value: "",
              position: "center",
              angle: -90,
              offset: 12,
              style: TYPOGRAPHY_VARIANTS["p2-medium-upper"] as {}
            }}
          />
          {legendData.map(legend => (
            <Bar
              isAnimationActive={isAnimationActive}
              key={legend.label}
              dataKey={legend.label}
              fill={legend.background}
              fillOpacity={legend.fillOpacity}
              stackId={xAxisKey}
              style={showValueOn === "click" ? { cursor: "pointer" } : {}}
              onClick={() => {
                if (!isDraggingRef.current) {
                  setShowValueFor(legend.label);
                }
              }}
              onMouseDown={data =>
                handleMouseDown(data.payload[UNIQUE_X_AXIS_IDENTIFIER])
              }
            >
              {shouldShowValueLabels &&
              (showValueOn !== "click" || showValueFor === legend.label) ? (
                <LabelList
                  dataKey={legend.label}
                  angle={valueLabelAngle}
                  formatter={tooltipFormatter("BAR_CHART", probableDataType)}
                  fill="var(--neutral-100)"
                  style={{
                    textAnchor: "middle"
                  }}
                />
              ) : null}
              {orderedDataWithUniqueXAxis.map((item, itemIndex) => (
                <Cell
                  key={`cell-${itemIndex}`}
                  cursor={getCellCursor()}
                  style={getCellColor(item[UNIQUE_X_AXIS_IDENTIFIER] as string)}
                />
              ))}
            </Bar>
          ))}
        </ReBarChart>
      </ResponsiveContainer>
    </CustomLegendWrapper>
  );
};
